import React, { useState, useEffect, useRef } from "react";
import "./NoticeDetailPopup.css";
import * as noticeDetailFunctions from "../../../../functions/Functions";
import axios from "axios";
import Swal from "sweetalert2";
import { getServerBaseUrl } from "../../../../utils/portConfig";
import Editor from "../../../../components/Dashboard/Editor";

function NoticeDetailPopup({ data, onClose, onDelete, selectedRow, token, userId, getData }) {
  const [originalData, setOriginalData] = useState(data); //수정된 데이터를 originalData로 초기화
  const [buttonText, setButtonText] = useState("수정");
  const [isEditor, setIsEditor] = useState(false);
  const [editedTitle, setEditedTitle] = useState(data.title);
  const [fileToUpload, setFileToUpload] = useState(data.attachments.length > 0 ? data.attachments[0] : null);
  const [deleteFileId, setDeleteFileId] = useState(null);
  const inputRef = useRef(null);

  // 에디터 ref 선언
  const editorRef = useRef(null);

  useEffect(() => {
    if (isEditor && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditor]);

  const handleOverlayClick = (e) => {
    if (e.target.className === "notice-popup-overlay") {
      onClose();
    }
  };

  const fileDownload = () => {
    noticeDetailFunctions.fileDownload(originalData.attachments[0], token);
  };

  const fileDelete = () => {
    if (originalData.attachments.length > 0) {
      setDeleteFileId(originalData.attachments[0].id);
    }
    setFileToUpload(null);
    setOriginalData((prevData) => ({
      ...prevData,
      attachments: [],
    }));
  };

  const noticeModifyButton = async () => {
    let updatedContents = "";

    if (editorRef.current) {
      const html = editorRef.current.getHTML();
      updatedContents = html;
    }

    if (buttonText === "저장") {
      if (editedTitle.trim() === "") {
        Swal.fire("알림", "제목을 입력해주세요.", "warning");
        return;
      }
      if (updatedContents === "" || updatedContents === "<p><br></p>") {
        Swal.fire("알림", "공지 내용을 입력해주세요.", "warning");
        return;
      }

      const confirmationResult = await Swal.fire({
        icon: "question",
        title: "공지 수정",
        text: "공지를 수정하시겠습니까?",
        showCancelButton: true,
        confirmButtonText: "확인",
        cancelButtonText: "취소",
      });

      if (confirmationResult.isConfirmed) {
        try {
          const formUpdateData = new FormData();
          formUpdateData.append("title", editedTitle.trim());
          formUpdateData.append("contents", updatedContents);

          if (fileToUpload) {
            if (fileToUpload instanceof File) {
              formUpdateData.append("newFiles", fileToUpload);
            }
          }

          if (deleteFileId) {
            let deleteFileArray = [];
            deleteFileArray.push(deleteFileId);
            formUpdateData.append("deleteAttachmentIds", deleteFileArray);
          }

          const url = `/api/v1/bbs/${selectedRow.id}/update/${userId}`;
          const response = await axios.put(url, formUpdateData, {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
          });
          if (response.status === 200) {
            Swal.fire({
              icon: "success",
              title: "성공",
              text: "공지가 수정되었습니다.",
            }).then(async () => {
              getData();
              setButtonText("수정");
              setIsEditor(false);

              // refreshDetails 함수 호출: 데이터 새로고침
              const refreshedData = await noticeDetailFunctions.refreshDetails(`${getServerBaseUrl()}/api/v1/bbs/${originalData.id}/${userId}`, token);
              setOriginalData((prevData) => ({
                ...prevData,
                contents: refreshedData.contents,
                title: refreshedData.title,
                attachments: [...refreshedData.attachments],
              }));
              setEditedTitle(refreshedData.title);
              setFileToUpload(refreshedData.attachments.length > 0 ? refreshedData.attachments[0] : null);
              setDeleteFileId(null);
              //파일 업로드 관련 로직 수정 필요
            });
          } else {
            Swal.fire({
              icon: "error",
              title: "실패",
              text: "공지 수정 실패. 다시 시도해주세요.",
            }).then(() => {
              setIsEditor(true);
              setButtonText("저장");
            });
          }
        } catch (error) {
          console.error("Error updating notice:", error);
          if (error.response) {
            const responseData = error.response.data;

            if (responseData) {
              Swal.fire({
                icon: "error",
                title: "공지 등록 오류",
                text: responseData,
              });
            } else {
              Swal.fire({
                icon: "error",
                title: "서버 응답 오류",
                text: `서버 응답 오류: ${error.response.status}`,
              });
            }
          } else {
            Swal.fire({
              icon: "error",
              title: "서버 응답이 없습니다.",
              text: "서버 응답이 없습니다.",
            });
          }
        }
      } else {
        // setEditedTitle(originalData.title);
        // originalData.contents = originalData.contents;
      }
    } else {
      setButtonText(isEditor ? "수정" : "저장");
      setIsEditor(!isEditor);
    }
  };

  const handleTitleChange = (e) => {
    setEditedTitle(e.target.value);
  };

  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files[0];
    setFileToUpload(selectedFile);
  };

  const handleNoticeDetailQuillChange = (content, delta, source, editor) => {
    if (editor.getText() === "\n") {
      originalData.contents = "";
    } else {
      originalData.contents = content;
    }
  };

  const renderTitle = () => {
    if (isEditor) {
      return <h2 className="notice-popup-header">게시글 수정</h2>;
    } else {
      return <h2 className="notice-popup-header">{editedTitle}</h2>;
    }
  };

  const renderNoticeInfo = () => {
    if (!isEditor) {
      return (
        <div className="notice-popup-info">
          <span>조회수: {originalData.viewsCount} 회</span>
          <span>작성일: {originalData.regDate}</span>
          <span>작성자: {originalData.regUser}</span>
        </div>
      );
    }
    return null;
  };

  const renderContents = () => {
    if (isEditor) {
      return (
        <div className="notice-popup-content-quill">
          <Editor ref={editorRef} readOnly={false} initialHTML={originalData.contents} placeholder="등록할 공지 내용을 입력하세요." />
        </div>
      );
    } else {
      return <div className="notice-popup-content" dangerouslySetInnerHTML={{ __html: originalData.contents }} />;
    }
  };

  const renderAttachments = () => {
    if (isEditor && originalData.attachments.length !== 0) {
      return (
        <div>
          <div>
            <input ref={inputRef} type="text" value={editedTitle} onChange={handleTitleChange} className="notice-popup-title" placeholder="제목을 입력해 주세요." />
          </div>
          <div className="notice-popup-file">
            <span>
              첨부파일: {renderLongFileNameWEdit(originalData.attachments[0].filename)}
              <span className="fileDelete" onClick={fileDelete}>
                X
              </span>
            </span>
          </div>
        </div>
      );
    } else if (isEditor && originalData.attachments.length === 0) {
      return (
        <div>
          <div>
            <input ref={inputRef} type="text" value={editedTitle} onChange={handleTitleChange} className="notice-popup-title" placeholder="제목을 입력해 주세요." />
          </div>
          <div className="notice-popup-file">
            <span>
              첨부파일: <input type="file" id="noticeDetailInputId" name="noticeDetailInput" onChange={handleFileInputChange} />
            </span>
          </div>
        </div>
      );
    }
  };

  const renderAttachmentsNotEdit = () => {
    if (!isEditor && originalData.attachments.length !== 0) {
      return (
        <div className="notice-popup-file-list">
          <span>첨부파일: </span>
          <span>
            &nbsp;
            <a className="attachments-style" onClick={fileDownload}>
              {renderLongFileNameWEdit(originalData.attachments[0].filename)}
            </a>
          </span>
        </div>
      );
    } else if (!isEditor && originalData.attachments.length === 0) {
      return (
        <div className="notice-popup-file-list">
          <span>첨부파일: </span>
          <span></span>
        </div>
      );
    }
    return null;
  };

  const renderLongFileNameWEdit = (fileName) => {
    if (fileName.length > 37) {
      return fileName.substring(0, 37) + "..." + fileName.match(/\.[^.]+$/)[0].slice(1);
    } else {
      return fileName;
    }
  };

  const renderDeleteButton = () => {
    if (!isEditor) {
      return (
        <button className="notice-popup-delete-button" onClick={onDelete}>
          삭제
        </button>
      );
    } else {
      return null;
    }
  };

  return (
    <div className="notice-popup-overlay" onClick={handleOverlayClick}>
      <div className="notice-popup">
        {renderTitle()}
        <div className="notice-popup-body">
          {renderAttachments()}
          {renderNoticeInfo()}
          {renderContents()}
          {renderAttachmentsNotEdit()}
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            {/*여백을 만들기 위해서 빈 div 추가*/}
            <div style={{ width: "70px" }}></div>
            <div>
              <button className="notice-popup-modify-button" onClick={noticeModifyButton}>
                {buttonText}
              </button>
              {renderDeleteButton()}
            </div>
            <div>
              <button onClick={onClose}>닫기</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NoticeDetailPopup;
